import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Building2, Calendar, TrendingUp, TrendingDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { formatInTimeZone } from 'date-fns-tz';
import { getShopDayLabel } from '@/utils/shopTime';
import { parseISO } from 'date-fns';
// DTC-K: route trends-window anchors through the canonical clock so
// analytics queries are deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';

interface ShopMetricsTrendsProps {
  shopId: string;
}

type TimeRange = "7d" | "30d" | "90d";

export function ShopMetricsTrends({ shopId }: ShopMetricsTrendsProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>("30d");
  const { shopTimezone } = useShopTimezone();

  const { data: trendData, isLoading } = useQuery({
    // IV-D-22 R75 FIX-2: include shopTimezone in the key — the query body
    // uses it at lines 35, 44-45, 57-58, 73 for date formatting. Previously
    // an admin-level TZ change left stale trendData with bounds in the old TZ.
    queryKey: ['shop-metrics-trends', shopId, timeRange, shopTimezone],
    queryFn: async () => {
      const daysBack = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
      const endDate = clock.now();
      const startDate = clock.now();
      startDate.setDate(startDate.getDate() - daysBack);

      // R9-H-1: Generate date range using shop TZ
      const dates: string[] = [];
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        dates.push(formatInTimeZone(d, shopTimezone, 'yyyy-MM-dd'));
      }

      // Fetch advisor metrics for the period
      const { data: advisorMetrics, error } = await supabase
        .from('advisor_daily_metrics')
        .select('*')
        .eq('shop_id', shopId)
        // R9-H-1: Use shop TZ for query bounds
        .gte('date', formatInTimeZone(startDate, shopTimezone, 'yyyy-MM-dd'))
        .lte('date', formatInTimeZone(endDate, shopTimezone, 'yyyy-MM-dd'))
        .order('date', { ascending: true });

      if (error) throw error;

      // Also fetch tickets for avg hours per ticket calculations
      // FIX-R131-MED-1: previously missing `.neq('status', 'Deleted')`. The
      // status==='Completed' branch increments completedCount, but EVERY
      // other status — including 'Deleted' — falls into the else branch
      // and inflates `openCount` + `totalPendingMinutes`. The
      // `avgPendingHoursPerTicket` chart line was therefore polluted by
      // deleted tickets within the date window. Other analytics surfaces
      // (Analytics.tsx:92, ShopHealthDashboard) already exclude Deleted —
      // aligning this query to the same convention.
      const { data: ticketsData, error: ticketsError } = await supabase
        .from('tickets')
        // R9-H-1: Include scheduled_date for bucketing
        .select('id, status, start_time, predicted_minutes, actual_minutes, scheduled_date')
        .eq('shop_id', shopId)
        // DMC-D3a: canonical active-ticket filter — both reps excluded.
        // The R131-MED-1 status-only filter remains; the deleted_at
        // exclusion is added defense-in-depth atop the DMC-D1 primitive
        // fix so any historical ghost row (status=Deleted AND
        // deleted_at IS NULL pre-backfill) cannot pollute the avg
        // pending hours per ticket calculation.
        .neq('status', 'Deleted')
        .is('deleted_at', null)
        // R10-M-2: Filter by scheduled_date (shop-day native), not UTC start_time
        .gte('scheduled_date', formatInTimeZone(startDate, shopTimezone, 'yyyy-MM-dd'))
        .lte('scheduled_date', formatInTimeZone(endDate, shopTimezone, 'yyyy-MM-dd'));

      if (ticketsError) throw ticketsError;

      // Build per-day ticket metrics map
      const ticketMetricsByDate = new Map<string, {
        completedCount: number;
        totalCompletedMinutes: number;
        openCount: number;
        totalPendingMinutes: number;
      }>();

      ticketsData?.forEach(ticket => {
        // R9-H-1: Use scheduled_date for bucketing; fallback via shop TZ
        const ticketDate = ticket.scheduled_date ??
          (ticket.start_time ? getShopDayLabel(ticket.start_time, shopTimezone) : null);
        if (!ticketDate) return;
        
        const existing = ticketMetricsByDate.get(ticketDate) || {
          completedCount: 0,
          totalCompletedMinutes: 0,
          openCount: 0,
          totalPendingMinutes: 0
        };
        
        if (ticket.status === 'Completed') {
          existing.completedCount++;
          existing.totalCompletedMinutes += ticket.actual_minutes || 0;
        } else {
          existing.openCount++;
          const pendingMinutes = Math.max(
            (ticket.predicted_minutes || 0) - (ticket.actual_minutes || 0),
            0
          );
          existing.totalPendingMinutes += pendingMinutes;
        }
        
        ticketMetricsByDate.set(ticketDate, existing);
      });

      // Aggregate by date
      const aggregated = dates.map(date => {
        const dayMetrics = advisorMetrics?.filter(m => m.date === date) || [];
        
        const totals = dayMetrics.reduce((acc, curr) => ({
          tickets: (acc.tickets || 0) + (curr.tickets_created || 0),
          conversions: (acc.conversions || 0) + (curr.waiter_conversions || 0),
          overrides: (acc.overrides || 0) + (curr.scheduling_overrides || 0),
          waiterCount: (acc.waiterCount || 0) + (curr.waiter_count || 0),
        }), { tickets: 0, conversions: 0, overrides: 0, waiterCount: 0 });

        const conversionRate = totals.waiterCount > 0
          ? Math.round((totals.conversions / totals.waiterCount) * 100)
          : 0;

        // Carry per-day conversion inputs so the period-level "Avg Conv Rate"
        // can be a weighted ratio (total conversions / total waiters), not a
        // plain average of daily percentages.
        const conversionNumerator = totals.conversions;
        const conversionDenominator = totals.waiterCount;

        // Get ticket metrics for this date
        const ticketDayMetrics = ticketMetricsByDate.get(date) || {
          completedCount: 0,
          totalCompletedMinutes: 0,
          openCount: 0,
          totalPendingMinutes: 0
        };

        const avgCompletedHoursPerTicket = ticketDayMetrics.completedCount > 0
          ? (ticketDayMetrics.totalCompletedMinutes / ticketDayMetrics.completedCount) / 60
          : 0;

        const avgPendingHoursPerTicket = ticketDayMetrics.openCount > 0
          ? (ticketDayMetrics.totalPendingMinutes / ticketDayMetrics.openCount) / 60
          : 0;

        return {
          // R9-H-1: Parse as local midnight to avoid UTC shift in display
          date: parseISO(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          tickets: totals.tickets,
          conversions: totals.conversions,
          conversionRate,
          conversionNumerator,
          conversionDenominator,
          overrides: totals.overrides,
          avgCompletedHoursPerTicket: Number(avgCompletedHoursPerTicket.toFixed(2)),
          avgPendingHoursPerTicket: Number(avgPendingHoursPerTicket.toFixed(2))
        };
      });

      return aggregated;
    }
  });

  // IV-D-22 R72 FIX-8: use a weighted period ratio (total conversions / total
  // waiters) instead of a simple mean of daily rates. The old mean treated a
  // day with 2 waiters the same as a day with 200, so a single low-volume
  // good day could mask weeks of poor conversion on high-volume days.
  const stats = trendData ? (() => {
    const totalConversions = trendData.reduce((sum, d) => sum + d.conversions, 0);
    const totalWaiters = trendData.reduce((sum, d) => sum + (d.conversionDenominator || 0), 0);
    return {
      totalTickets: trendData.reduce((sum, d) => sum + d.tickets, 0),
      totalConversions,
      avgConversionRate: totalWaiters > 0
        ? Math.round((totalConversions / totalWaiters) * 100)
        : 0,
      totalOverrides: trendData.reduce((sum, d) => sum + d.overrides, 0)
    };
  })() : null;

  if (isLoading) {
    return <Card className="p-4"><div className="h-96 animate-pulse bg-muted rounded" /></Card>;
  }

  if (!trendData || trendData.length === 0) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Shop Activity Trends</h3>
        </div>
        <p className="text-sm text-muted-foreground">No shop metrics available yet</p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Shop Activity Trends</h3>
        </div>
        <Select value={timeRange} onValueChange={(v) => setTimeRange(v as TimeRange)}>
          <SelectTrigger className="w-32">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">7 Days</SelectItem>
            <SelectItem value="30d">30 Days</SelectItem>
            <SelectItem value="90d">90 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Total Tickets</p>
            <p className="font-mono text-lg font-bold text-foreground">{stats.totalTickets}</p>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Conversions</p>
            <p className="font-mono text-lg font-bold text-foreground">{stats.totalConversions}</p>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Avg Conv. Rate</p>
            <div className="flex items-center gap-2">
              <p className="font-mono text-lg font-bold text-foreground">{stats.avgConversionRate}%</p>
              {stats.avgConversionRate >= 50 ? (
                <Badge variant="default" className="gap-1">
                  <TrendingUp className="h-3 w-3" />
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <TrendingDown className="h-3 w-3" />
                </Badge>
              )}
            </div>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Overrides</p>
            <p className="font-mono text-lg font-bold text-foreground">{stats.totalOverrides}</p>
          </div>
        </div>
      )}

      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={trendData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis 
            dataKey="date" 
            className="text-xs text-muted-foreground"
            tick={{ fill: 'hsl(var(--muted-foreground))' }}
          />
          <YAxis 
            className="text-xs text-muted-foreground"
            tick={{ fill: 'hsl(var(--muted-foreground))' }}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '0.5rem',
              color: 'hsl(var(--foreground))'
            }}
          />
          <Legend 
            wrapperStyle={{
              color: 'hsl(var(--foreground))'
            }}
          />
          <Area 
            type="monotone" 
            dataKey="tickets" 
            stackId="1"
            stroke="hsl(var(--primary))" 
            fill="hsl(var(--primary))"
            fillOpacity={0.6}
            name="Tickets Created"
          />
          <Area 
            type="monotone" 
            dataKey="conversions" 
            stackId="2"
            stroke="hsl(var(--success))" 
            fill="hsl(var(--success))"
            fillOpacity={0.6}
            name="Waiter Conversions"
          />
          <Area 
            type="monotone" 
            dataKey="overrides" 
            stackId="3"
            stroke="hsl(var(--destructive))" 
            fill="hsl(var(--destructive))"
            fillOpacity={0.6}
            name="Scheduling Overrides"
          />
          <Area 
            type="monotone" 
            dataKey="avgCompletedHoursPerTicket" 
            stroke="hsl(var(--chart-3))" 
            fill="hsl(var(--chart-3))"
            fillOpacity={0.4}
            name="Avg Completed Hrs/Ticket"
          />
          <Area 
            type="monotone" 
            dataKey="avgPendingHoursPerTicket" 
            stroke="hsl(var(--chart-4))" 
            fill="hsl(var(--chart-4))"
            fillOpacity={0.4}
            name="Avg Pending Hrs/Ticket"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
}
